<template>
  <component
    :is="tag"
    :class="rootClasses"
    :type="tag === 'button' ? type : undefined"
    v-bind="$attrs"
  >
    <slot></slot>
  </component>
</template>

<script>
  export default {
    name: 'BaseButton',
    props: {
      variant: {
        type: String,
        default: 'primary'
      },
      size: {
        type: String,
        default: null
      },
      block: {
        type: Boolean,
        default: false
      },
      constrained: {
        type: Boolean,
        default: false
      },
      unstyled: {
        type: Boolean,
        default: false
      },
      bare: {
        type: Boolean,
        default: false
      },
      tag: {
        type: String,
        default: 'button'
      },
      type: {
        type: String,
        default: 'button'
      }
    },
    computed: {
      rootClasses() {
        const classes = []
        if (!this.bare) classes.push('btn')
        return classes.concat(this.buttonClasses)
      },
      buttonClasses() {
        const classes = []
        const variant = this.variant ? (this.variant.startsWith('btn-') ? this.variant : `btn-${this.variant}`) : null
        const applyBase = !this.unstyled && !this.bare

        if (variant) classes.push(variant)
        if (applyBase) classes.push('base-button')
        if (this.size && this.size !== 'md') classes.push(`size-${this.size}`)
        if (this.block) classes.push('btn-block')
        if (this.constrained) classes.push('btn-constrained')

        return classes
      }
    }
  }
</script>

<style scoped lang="scss">
  .base-button {
    padding: 0.75rem 2rem;
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.01em;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    -webkit-tap-highlight-color: transparent;
    -webkit-user-select: none;
    user-select: none;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .base-button.size-sm {
    padding: 0.5rem 1rem;
    font-size: var(--fs-xs);
    font-weight: var(--fw-medium);
    letter-spacing: -0.01em;
    border-radius: 6px;
    min-height: 44px;
  }

  .base-button.size-lg {
    padding: 0.75rem 2.5rem;
    font-size: var(--fs-base);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.02em;
    min-height: 48px;
  }

  .base-button.btn-primary {
    background-color: #f58a48 !important;
    border-color: #f58a48 !important;
    color: white;

    /* Only apply hover effects on devices with hover capability (not touch) */
    @media (hover: hover) {
      &:hover:not(:disabled) {
        background-color: #e67e3e !important;
        border-color: #e67e3e !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(245, 138, 72, 0.2);
        -webkit-box-shadow: 0 4px 12px rgba(245, 138, 72, 0.2);
      }
    }

    /* Active state for touch devices */
    &:active:not(:disabled) {
      background-color: #e67e3e !important;
      transform: scale(0.98);
    }
  }

  .base-button.btn-outline-secondary {
    background: white;
    color: #6c757d;
    border: 1.5px solid #dee2e6;

    /* Only apply hover effects on devices with hover capability */
    @media (hover: hover) {
      &:hover:not(:disabled) {
        background: #f8f9fa;
        border-color: #adb5bd;
      }
    }

    /* Active state for touch devices */
    &:active:not(:disabled) {
      background: #f8f9fa;
      transform: scale(0.98);
    }
  }

  .base-button.btn-block {
    width: 100%;
  }

  .base-button.btn-constrained {
    max-width: 250px;
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  .mt-3 {
    margin-top: 1rem;
  }

  .me-2 {
    margin-right: 0.5rem;
  }

  .spinner-border {
    display: inline-block;
    width: 0.875rem;
    height: 0.875rem;
    vertical-align: text-bottom;
    border: 0.125em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border 0.75s linear infinite;
  }

  .spinner-border-sm {
    width: 0.75rem;
    height: 0.75rem;
    border-width: 0.1em;
  }

  @keyframes spinner-border {
    to {
      transform: rotate(360deg);
    }
  }
</style>
